import { useState, useRef, forwardRef } from 'react'
import { CameraIcon, PhotoIcon, XMarkIcon, ArrowPathIcon, LightBulbIcon } from '@heroicons/react/24/outline'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { weeklyProgramsAPI } from '../../services/api'

const PHOTO_ANALYSIS_TIMEOUT_MS = 45000

const WorkoutPhotoUpload = forwardRef(({ session, onAnalysisComplete, onCancel }, _ref) => {
  const { t } = useTranslation()
  const [selectedFile, setSelectedFile] = useState(null)
  const [previewUrl, setPreviewUrl] = useState(null)
  const [uploadMode, setUploadMode] = useState(null) // 'camera' | 'file' | 'folder'
  const [analyzer, setAnalyzer] = useState('gpt') // 'gpt' or 'google'
  const [analysisPhase, setAnalysisPhase] = useState(null) // 'uploading' | 'analyzing'
  const [showTips, setShowTips] = useState(false)
  const [lastError, setLastError] = useState(null)
  const fileInputRef = useRef(null)
  const abortControllerRef = useRef(null)

  // Mutation for photo analysis
  const analyzeMutation = useMutation({
    mutationFn: ({ formData, analyzerType }) => {
      setAnalysisPhase('uploading')
      // Create abort controller for timeout
      const controller = new AbortController()
      abortControllerRef.current = controller
      const timeoutId = setTimeout(() => controller.abort(), PHOTO_ANALYSIS_TIMEOUT_MS)

      return weeklyProgramsAPI.analyzeWorkoutPhoto(session.id, formData, analyzerType, controller.signal)
        .finally(() => clearTimeout(timeoutId))
    },
    onMutate: () => {
      setLastError(null)
      setAnalysisPhase('uploading')
      // Switch to "analyzing" phase after a short delay (upload usually fast)
      setTimeout(() => setAnalysisPhase((prev) => prev ? 'analyzing' : null), 1500)
    },
    onError: (error) => {
      setAnalysisPhase(null)
      const message = error.name === 'CanceledError' || error.code === 'ERR_CANCELED'
        ? t('workouts.photo.errors.timeout', 'Analysis timed out. Please try again.')
        : error.response?.data?.detail || t('workouts.photo.errors.analysisFailed', 'Failed to analyze photo. Please try again.')
      setLastError(message)
      toast.error(message)
    },
    onSuccess: () => {
      setAnalysisPhase(null)
    }
  })

  // Handle file selection
  const handleFileSelect = (event) => {
    const file = event.target.files[0]
    if (file) {
      processFile(file)
    }
  }

  // Process selected file
  const processFile = (file) => {
    // Validate file type - check both MIME type and file extension
    // Some browsers/devices (especially iOS) may not report MIME types correctly
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/heic', 'image/heif']
    const allowedExtensions = ['.jpg', '.jpeg', '.png', '.heic', '.heif']
    const fileName = file.name?.toLowerCase() || ''
    const hasValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext))
    const hasValidType = allowedTypes.includes(file.type) || file.type === '' || file.type.startsWith('image/')

    if (!hasValidExtension && !hasValidType) {
      toast.error(t('workouts.photo.errors.invalidFileType', 'Please select a JPG, PNG, or HEIC image file.'))
      return
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024 // 10MB
    if (file.size > maxSize) {
      toast.error(t('workouts.photo.errors.fileTooLarge', 'File is too large. Maximum size is 10MB.'))
      return
    }

    setSelectedFile(file)

    // Create preview URL
    const reader = new FileReader()
    reader.onloadend = () => {
      setPreviewUrl(reader.result)
    }
    reader.onerror = () => {
      toast.error(t('workouts.photo.errors.readError', 'Failed to read the image file. Please try again.'))
    }
    reader.readAsDataURL(file)
  }

  // Handle upload and analysis
  const handleAnalyze = () => {
    if (!selectedFile) return

    const formData = new FormData()
    formData.append('file', selectedFile)

    analyzeMutation.mutate({ formData, analyzerType: analyzer }, {
      onSuccess: (response) => {
        if (onAnalysisComplete) {
          onAnalysisComplete(response.data)
        }
      }
    })
  }

  // Clear selection
  const handleClear = () => {
    setSelectedFile(null)
    setPreviewUrl(null)
    setUploadMode(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  return (
    <div className="space-y-4">
      {/* Upload Mode Selection */}
      {!uploadMode && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Camera Option */}
          <button
            onClick={() => {
              setUploadMode('camera')
              fileInputRef.current?.click()
            }}
            className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-300 rounded-lg hover:border-indigo-500 hover:bg-indigo-50 transition-colors"
          >
            <CameraIcon className="h-12 w-12 text-gray-400 mb-3" />
            <span className="text-sm font-medium text-gray-900">
              {t('workouts.photo.takePhoto', 'Take Photo')}
            </span>
            <span className="text-xs text-gray-500 mt-1">
              {t('workouts.photo.useCamera', 'Use your camera')}
            </span>
          </button>

          {/* File Upload Option */}
          <button
            onClick={() => {
              setUploadMode('file')
              fileInputRef.current?.click()
            }}
            className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-300 rounded-lg hover:border-indigo-500 hover:bg-indigo-50 transition-colors"
          >
            <PhotoIcon className="h-12 w-12 text-gray-400 mb-3" />
            <span className="text-sm font-medium text-gray-900">
              {t('workouts.photo.uploadPhoto', 'Upload Photo')}
            </span>
            <span className="text-xs text-gray-500 mt-1">
              {t('workouts.photo.chooseFromDevice', 'Choose from device')}
            </span>
          </button>
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".jpg,.jpeg,.png,.heic,.heif,image/*"
        capture={uploadMode === 'camera' ? 'environment' : undefined}
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Loading state */}
      {uploadMode && !previewUrl && (
        <div className="flex flex-col items-center justify-center p-12 border-2 border-dashed border-gray-300 rounded-lg">
          <svg className="animate-spin h-12 w-12 text-indigo-600 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-gray-600">{t('workouts.photo.loadingPhoto', 'Loading photo...')}</p>
        </div>
      )}

      {/* Preview and Analysis */}
      {previewUrl && (
        <div className="space-y-4">
          <div className="relative">
            <img
              src={previewUrl}
              alt="Workout sheet preview"
              className="w-full max-h-96 object-contain rounded-lg border-2 border-gray-200"
            />
            <button
              onClick={handleClear}
              className="absolute top-2 right-2 p-2 bg-surface-container rounded-full shadow-lg hover:bg-gray-100"
            >
              <XMarkIcon className="h-5 w-5 text-gray-600" />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              <p className="font-medium">{selectedFile?.name}</p>
              <p className="text-xs text-gray-500">
                {(selectedFile?.size / 1024).toFixed(1)} KB
              </p>
            </div>
          </div>

          {/* Analyzer Selection Toggle */}
          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
            <label className="block text-xs font-medium text-gray-700 mb-2">
              {t('workouts.photo.analyzerSelection', 'OCR Engine')}
            </label>
            <div className="flex space-x-2">
              <button
                type="button"
                onClick={() => setAnalyzer('gpt')}
                className={`flex-1 px-3 py-2 text-sm rounded-md transition-colors ${
                  analyzer === 'gpt'
                    ? 'bg-indigo-600 text-on-neutral'
                    : 'bg-surface-container text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                GPT-4 Vision
              </button>
              <button
                type="button"
                onClick={() => setAnalyzer('google')}
                className={`flex-1 px-3 py-2 text-sm rounded-md transition-colors ${
                  analyzer === 'google'
                    ? 'bg-indigo-600 text-on-neutral'
                    : 'bg-surface-container text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                Google Vision
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {analyzer === 'gpt'
                ? t('workouts.photo.gptDescription', 'Best for general OCR (default)')
                : t('workouts.photo.googleDescription', 'May be better at distinguishing fonts')}
            </p>
          </div>

          {/* Photo Tips */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg">
            <button
              type="button"
              onClick={() => setShowTips(!showTips)}
              className="w-full flex items-center justify-between px-3 py-2 text-sm text-amber-800"
            >
              <span className="flex items-center gap-2">
                <LightBulbIcon className="h-4 w-4" />
                {t('workouts.photo.tipsTitle', 'Photo Tips')}
              </span>
              <span className="text-xs">{showTips ? '-' : '+'}</span>
            </button>
            {showTips && (
              <ul className="px-3 pb-3 text-xs text-amber-700 space-y-1 list-disc list-inside">
                <li>{t('workouts.photo.tips.flat', 'Lay the sheet flat on a table')}</li>
                <li>{t('workouts.photo.tips.shadows', 'Avoid shadows over the writing')}</li>
                <li>{t('workouts.photo.tips.columns', 'Ensure all columns are visible')}</li>
                <li>{t('workouts.photo.tips.lighting', 'Use good, even lighting')}</li>
              </ul>
            )}
          </div>

          {/* Error with Retry */}
          {lastError && !analyzeMutation.isPending && (
            <div className="flex items-center justify-between bg-red-50 border border-red-200 rounded-lg px-4 py-3">
              <p className="text-sm text-red-700">{lastError}</p>
              <button
                onClick={handleAnalyze}
                className="flex items-center gap-1 ml-3 px-3 py-1.5 text-sm font-medium text-red-700 bg-red-100 hover:bg-red-200 rounded-md transition-colors"
              >
                <ArrowPathIcon className="h-4 w-4" />
                {t('workouts.photo.retry', 'Retry')}
              </button>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex space-x-3">
            <button
              onClick={handleAnalyze}
              disabled={analyzeMutation.isPending}
              className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {analyzeMutation.isPending ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {analysisPhase === 'uploading'
                    ? t('workouts.photo.uploading', 'Uploading...')
                    : t('workouts.photo.analyzing', 'Analyzing...')}
                </>
              ) : (
                t('workouts.photo.analyzeWorkout', 'Analyze Workout Sheet')
              )}
            </button>
            <button
              onClick={handleClear}
              disabled={analyzeMutation.isPending}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {t('workouts.photo.chooseDifferent', 'Choose Different Photo')}
            </button>
          </div>
        </div>
      )}

      {/* Cancel Button */}
      {onCancel && (
        <div className="flex justify-end">
          <button
            onClick={onCancel}
            className="text-sm text-gray-600 hover:text-gray-900"
          >
            {t('common.cancel')}
          </button>
        </div>
      )}
    </div>
  )
})

WorkoutPhotoUpload.displayName = 'WorkoutPhotoUpload'

export default WorkoutPhotoUpload
